import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Container, Input, Label, Spinner, Collapse } from "reactstrap";
import { FileUploader } from "react-drag-drop-files";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { AlertCircle, ChevronDown, ChevronRight } from "react-feather";
import API from "../../../utils/API";
import CSVDownload from "../../../components/CsvDownload";

const TABLE_OPTIONS = [
    { value: "lead_refinance", label: "Refinance" },
    { value: "lead_solar", label: "Solar" },
    { value: "lead_home_seller", label: "HomeSeller" },
    { value: "lead_windows", label: "HomeImprovement – Windows" },
    { value: "lead_roofing", label: "HomeImprovement – Roofing" },
    { value: "lead_bath", label: "HomeImprovement – Bath" },
    { value: "lead_siding", label: "HomeImprovement – Siding" },
];

const ALL_TABLE_KEYS = TABLE_OPTIONS.map((option) => option.value);

const SPECIAL_OPTIONS = [
    { value: "full_lead", label: "Append ALL lead columns (full_lead)" },
];

const COMMON_GROUP_CONFIG = {
    PII: [
        "First Name",
        "Last Name",
        "Phone",
        "Email Address",
        "Street Address",
        "City",
        "State",
        "ZIP Code",
    ],
    Tokens: ["TrustedForm Token", "Jornaya Lead ID"],
    Earnings: ["Publisher Earning", "Total Earning", "System Earning"],
};

const AddReportColumns = () => {
    // Upload / CSV
    const [fileName, setFileName] = useState("");
    const [csvText, setCsvText] = useState(null);

    // Match key selection
    const [identifierType, setIdentifierType] = useState("lead_id");
    const [identifierColumn, setIdentifierColumn] = useState("");

    // UI state (collapses)
    const [openCommonSection, setOpenCommonSection] = useState(true);
    const [openVerticalSections, setOpenVerticalSections] = useState([]);

    // Schema data & loading
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [commonFields, setCommonFields] = useState({}); // display → backend
    const [verticalFields, setVerticalFields] = useState({}); // table → {display → backend}

    // User selections
    const [selectedCommonFields, setSelectedCommonFields] = useState([]); // [backend]
    const [selectedVerticalFields, setSelectedVerticalFields] = useState({}); // {table: [backend]}
    const [specials, setSpecials] = useState([]);

    // Derived state
    const hasFullLead = useMemo(() => specials.includes("full_lead"), [specials]);

    useEffect(() => {
        const loadSchema = async () => {
            setSchemaLoading(true);
            const res = await API.get("/admin/lead/expandedreportschema");
            setCommonFields(res.data?.data?.common || {});
            setVerticalFields(res.data?.data?.verticals || {});
            setSchemaLoading(false);
        };

        loadSchema();
    }, []);

    const handleFileUpload = (uploadedFile) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            setCsvText(event.target.result);
            setFileName(uploadedFile?.name || "uploaded.csv");
        };
        reader.readAsText(uploadedFile);
    };

    const handleToggleVerticalSection = (tableKey) => {
        setOpenVerticalSections((prev) =>
            prev.includes(tableKey) ? prev.filter((key) => key !== tableKey) : [...prev, tableKey]
        );
    };

    const handleToggleCommonField = (backendField) => {
        setSelectedCommonFields((prev) =>
            prev.includes(backendField)
                ? prev.filter((field) => field !== backendField)
                : [...prev, backendField]
        );
    };

    const handleToggleVerticalField = (tableKey, backendField) => {
        setSelectedVerticalFields((prev) => {
            const currentSelection = prev[tableKey] || [];
            const updatedSelection = currentSelection.includes(backendField)
                ? currentSelection.filter((field) => field !== backendField)
                : [...currentSelection, backendField];

            return { ...prev, [tableKey]: updatedSelection };
        });
    };

    const handleToggleSpecialOption = (optionValue) => {
        setSpecials((prev) =>
            prev.includes(optionValue)
                ? prev.filter((value) => value !== optionValue)
                : [...prev, optionValue]
        );
    };

    const groupedCommonLabels = useMemo(
        () => new Set(Object.values(COMMON_GROUP_CONFIG).flat()),
        []
    );

    const ungroupedCommonDisplays = useMemo(
        () => Object.keys(commonFields).filter((display) => !groupedCommonLabels.has(display)),
        [commonFields, groupedCommonLabels]
    );

    const addColumns = useMemo(() => {
        if (hasFullLead) return [];

        const orderedBackendFields = [];

        Object.keys(commonFields).forEach((display) => {
            const backend = commonFields[display];
            if (selectedCommonFields.includes(backend)) orderedBackendFields.push(backend);
        });

        ALL_TABLE_KEYS.forEach((tableKey) => {
            const fieldMap = verticalFields[tableKey] || {};
            Object.keys(fieldMap).forEach((display) => {
                const backend = fieldMap[display];
                const selectedForTable = selectedVerticalFields[tableKey] || [];
                if (selectedForTable.includes(backend)) orderedBackendFields.push(backend);
            });
        });

        return orderedBackendFields;
    }, [hasFullLead, selectedCommonFields, selectedVerticalFields, commonFields, verticalFields]);

    const canGenerate =
        !!csvText && identifierColumn.trim().length > 0 && (hasFullLead || addColumns.length > 0);

    const suggestedName = useMemo(
        () => (hasFullLead ? "added_columns_full_lead.csv" : "added_columns.csv"),
        [hasFullLead]
    );

    const downloadPayload = useMemo(
        () => ({
            csvData: csvText,
            identifierType,
            identifierColumn,
            tables: ALL_TABLE_KEYS,
            addColumns,
            specials,
            fileName: suggestedName,
        }),
        [csvText, identifierType, identifierColumn, addColumns, specials, suggestedName]
    );

    const renderCommonGroup = (groupLabel, displayKeys) => {
        const entries = displayKeys.filter((display) => commonFields[display]);
        if (!entries.length) return null;

        return (
            <div className="mb-2" key={groupLabel}>
                <div className="fw-bold my-2">- {groupLabel}</div>
                <div className="row m-2">
                    {entries.map((display, idx) => {
                        const backend = commonFields[display];
                        const id = `common-${groupLabel}-${idx}`;

                        return (
                            <div className="col-12 col-sm-6 col-md-4" key={display}>
                                <div className="d-flex align-items-center">
                                    <input
                                        id={id}
                                        type="checkbox"
                                        className="form-check-input mt-0"
                                        disabled={hasFullLead}
                                        checked={selectedCommonFields.includes(backend)}
                                        onChange={() => handleToggleCommonField(backend)}
                                    />
                                    <label htmlFor={id} className="ms-2 mb-0">
                                        {display}
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const collapsibleButtonStyle = {
        color: "black",
        fontWeight: "500",
        textDecoration: "none",
    };

    return (
        <Container fluid className="p-0">
            <Breadcrumb>
                <h1 className="h3 mb-1">Add Additional Columns to Report</h1>
            </Breadcrumb>

            {/* 1) Upload CSV */}
            <div className="card mb-3">
                <div className="card-header">
                    <strong>1) Upload CSV</strong>
                </div>
                <div className="card-body">
                    <FileUploader handleChange={handleFileUpload} name="file" types={["CSV"]} label="Upload or drop a CSV file here" />
                    {fileName && <div className="small text-muted mt-1">Loaded: {fileName}</div>}
                    <Tippy
                        content={
                            <span>
                                The CSV must include a column that contains either Volga lead ID, a
                                Jornaya LeadID token, or a TrustedForm token.
                            </span>
                        }
                    >
                        <span className="ms-2 align-middle">
                            <AlertCircle size={16} />
                        </span>
                    </Tippy>
                </div>
            </div>

            {/* 2) Identify match key */}
            <div className="card mb-3">
                <div className="card-body">
                    <div>
                        <strong>2) Identify match key</strong>
                    </div>
                    <div className="row mt-2">
                        <div className="col-12 col-md-3">
                            <Label className="mb-1">Match against</Label>
                            <select
                                className="form-control"
                                value={identifierType}
                                onChange={(event) => setIdentifierType(event.target.value)}
                            >
                                <option value="lead_id">Volga Lead ID</option>
                                <option value="leadid_token">Jornaya LeadID Token</option>
                                <option value="trustedform_token">TrustedForm Token</option>
                            </select>
                        </div>

                        <div className="col-12 col-md-6">
                            <Label className="mb-1">Column name in the CSV (exact)</Label>
                            <Input
                                placeholder={
                                    identifierType === "lead_id"
                                        ? "e.g. lead_id"
                                        : identifierType === "leadid_token"
                                            ? "e.g. leadid_token"
                                            : "e.g. trustedform_token"
                                }
                                value={identifierColumn}
                                onChange={(event) => setIdentifierColumn(event.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 3) Columns mode */}
            <div className="card mb-3">
                <div className="card-body">
                    <div>
                        <strong>3) Columns mode</strong>
                    </div>
                    <div className="row m-2">
                        {SPECIAL_OPTIONS.map((option) => (
                            <div className="col-12 col-sm-6 col-md-4" key={option.value}>
                                <div className="d-flex align-items-center">
                                    <input
                                        id={`special-${option.value}`}
                                        type="checkbox"
                                        className="form-check-input mt-0"
                                        checked={specials.includes(option.value)}
                                        onChange={() => handleToggleSpecialOption(option.value)}
                                    />
                                    <label htmlFor={`special-${option.value}`} className="ms-2 mb-0">
                                        {option.label}
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>

                    {hasFullLead && (
                        <div className="small text-muted mt-2">
                            <em>
                                full_lead will append all columns (plus <code>failed_reason</code>).
                                Common/Unique selections are ignored.
                            </em>
                        </div>
                    )}
                </div>
            </div>

            {/* 4) Columns to append */}
            <div className="card mb-3">
                <div className="card-body">
                    <div>
                        <strong>4) Columns to append</strong>
                    </div>

                    {schemaLoading ? (
                        <div className="d-flex align-items-center">
                            <Spinner size="sm" className="me-2" /> Loading schema…
                        </div>
                    ) : (
                        <>
                            {/* Common fields (grouped, collapsible) */}
                            <fieldset disabled={hasFullLead} aria-disabled={hasFullLead}>
                                <div className={`mb-3 ${hasFullLead ? "opacity-50" : ""}`}>
                                    <button
                                        type="button"
                                        className="w-100 text-start btn btn-link d-flex align-items-center gap-2 p-2"
                                        onClick={() => setOpenCommonSection((open) => !open)}
                                        aria-expanded={openCommonSection}
                                        aria-controls="collapse-common"
                                        disabled={hasFullLead}
                                        style={collapsibleButtonStyle}
                                    >
                                        {openCommonSection ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                        <span className="fw-bold">Common (all verticals)</span>
                                    </button>

                                    <Collapse isOpen={openCommonSection}>
                                        <div id="collapse-common" className="px-3 my-1">
                                            {renderCommonGroup("PII", COMMON_GROUP_CONFIG.PII)}
                                            {renderCommonGroup("Tokens", COMMON_GROUP_CONFIG.Tokens)}
                                            {renderCommonGroup("Earnings", COMMON_GROUP_CONFIG.Earnings)}
                                            {renderCommonGroup("Other Common Fields", ungroupedCommonDisplays)}
                                        </div>
                                    </Collapse>
                                </div>
                            </fieldset>

                            {/* Per-vertical unique fields */}
                            <fieldset disabled={hasFullLead} aria-disabled={hasFullLead}>
                                <div className={hasFullLead ? "opacity-50" : ""}>
                                    {TABLE_OPTIONS.map((option) => {
                                        const tableKey = option.value;
                                        const fieldMap = verticalFields[tableKey] || {};
                                        const isOpen = openVerticalSections.includes(tableKey);
                                        if (!Object.keys(fieldMap).length) return null;

                                        const selectedForTable = selectedVerticalFields[tableKey] || [];

                                        return (
                                            <div className="mb-2 border rounded" key={tableKey}>
                                                <button
                                                    type="button"
                                                    className="w-100 text-start btn btn-link d-flex align-items-center gap-2 p-2"
                                                    onClick={() => handleToggleVerticalSection(tableKey)}
                                                    aria-expanded={isOpen}
                                                    aria-controls={`collapse-${tableKey}`}
                                                    disabled={hasFullLead}
                                                    style={collapsibleButtonStyle}
                                                >
                                                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                    <span className="fw-bold">Unique – {option.label}</span>
                                                </button>

                                                <Collapse isOpen={isOpen}>
                                                    <div id={`collapse-${tableKey}`} className="px-3 my-1">
                                                        <div className="row m-2">
                                                            {Object.keys(fieldMap).map((display, idx) => {
                                                                const backend = fieldMap[display];
                                                                const id = `vertical-${tableKey}-${idx}`;

                                                                return (
                                                                    <div className="col-12 col-sm-6 col-md-4" key={`${tableKey}:${display}`}>
                                                                        <div className="d-flex align-items-center">
                                                                            <input
                                                                                id={id}
                                                                                type="checkbox"
                                                                                className="form-check-input mt-0"
                                                                                disabled={hasFullLead}
                                                                                checked={selectedForTable.includes(backend)}
                                                                                onChange={() =>
                                                                                    handleToggleVerticalField(tableKey, backend)
                                                                                }
                                                                            />
                                                                            <label htmlFor={id} className="ms-2 mb-0">
                                                                                {display}
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </div>
                                        );
                                    })}
                                </div>
                            </fieldset>
                        </>
                    )}
                </div>
            </div>

            {/* 5) Generate & Download */}
            <div className="card mb-3">
                <div className="card-body">
                    <strong>5) Generate &amp; Download CSV</strong>

                    {canGenerate ? (
                        <div className="mt-2">
                            <CSVDownload
                                url="/admin/lead/expandedreportcsv"
                                filename={suggestedName}
                                filtration={downloadPayload}
                                style={{ display: "inline-flex", width: "auto" }}
                            />
                        </div>
                    ) : (
                        <div className="mt-2 text-muted">
                            Fill out the form to enable download
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default AddReportColumns;